import { graphql } from 'react-relay';
import type { FC, MouseEvent } from 'react';
import React, { useEffect, Fragment } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import AddIcon from '@atlaskit/icon/core/add';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import type { PositionType } from '@atlaskit/tooltip';
import Tooltip from '@atlaskit/tooltip';

import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type {
	CreateButtonBaseSpaceOperationsQuery as CreateButtonBaseSpaceOperationsQuery$data,
	CreateButtonBaseSpaceOperationsQueryVariables as CreateButtonBaseSpaceOperationsQuery$variables,
} from './__types__/CreateButtonBaseSpaceOperationsQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export type Props = {
	canCreateContent: (permissions: CreateButtonBaseSpaceOperationsQuery$data) => boolean;
	onCreateContent?: ({ spaceId }: { spaceId: string }) => void;
	href?: string;
	labelI18n: MessageDescriptor;
	tooltipShortcut?: string;
	tooltipTestId?: string;
	spaceKey: string;
	tooltipPosition: PositionType | undefined;
	createPagePermissionCheck?: (canCreate: boolean | undefined) => (() => void) | undefined;
	pagesSideNavButtonStyleOverride?: boolean;
	testId?: string;
};

// This is changing the color only when the mouse pointer hovers over the icon
// itself. If you hover over the outskirts of the button, it'll darken but the
// icon won't. That's likely not intentional.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightGreyIconWrapper = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& svg': {
			color: token('color.icon.subtle'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:hover svg': {
			color: token('color.icon'),
		},
	},
});

export const CreateButtonBase: FC<Props> = ({
	canCreateContent,
	href,
	onCreateContent,
	tooltipTestId,
	labelI18n,
	tooltipShortcut,
	spaceKey,
	createPagePermissionCheck,
	tooltipPosition,
	pagesSideNavButtonStyleOverride = false,
	testId,
}) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const intl = useIntl();
	const isNav4Enabled = useIsNav4Enabled();
	const { data, loading, error } = useQueryInterop<
		CreateButtonBaseSpaceOperationsQuery$data,
		CreateButtonBaseSpaceOperationsQuery$variables
	>(
		graphql`
			query CreateButtonBaseSpaceOperationsQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					operations {
						operation
						targetType
					}
				}
			}
		`,
		{
			variables: { spaceKey },
		},
	);
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'page-tree-create-button-base',
	});
	const { push } = useRouteActions();

	const canCreate = data && canCreateContent(data);

	useEffect(() => createPagePermissionCheck?.(canCreate), [createPagePermissionCheck, canCreate]);

	const shortcutStrings = useShortcutStrings({ shortcut: tooltipShortcut });

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (loading || !canCreate) {
		return null;
	}

	const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		event.preventDefault();

		if (href) {
			if (onCreateContent) {
				const spaceId = data?.space?.id || '';
				onCreateContent({ spaceId });
			}

			if (event.metaKey) {
				window.open(href, '_blank', 'noreferrer');
			} else {
				push(href);
			}
		}
	};

	const IconWrapper =
		pagesSideNavButtonStyleOverride && !isNav4Enabled ? LightGreyIconWrapper : Fragment;

	return (
		<Tooltip
			content={intl.formatMessage(labelI18n)}
			shortcut={shortcutStrings}
			position={tooltipPosition}
			testId={tooltipTestId}
		>
			<IconWrapper>
				<IconButton
					icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
					label={intl.formatMessage(labelI18n)}
					onClick={enforceStorageLimit(handleButtonClick)}
					testId={testId}
					spacing="compact"
					appearance="subtle"
					isDisabled={renderServerPlaceholder}
				/>
			</IconWrapper>
		</Tooltip>
	);
};
